import {
  ChevronDownIcon,
  CursorArrowRaysIcon,
  LinkIcon,
  PlusIcon,
  TrashIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@superfunnel/ui-core/v2/Collapsible';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@superfunnel/ui-core/v2/Dialog';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import Input from '@superfunnel/ui-core/v2/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import Switch from '@superfunnel/ui-core/v2/Switch';
import {
  formFieldUrlWithParamsValidator,
  isValidUrlWithParams,
  UrlValidationConfig,
} from '@superfunnel/utils';
import { useCallback, useContext, useEffect, useState } from 'react';
import {
  FormOrQuizSelection,
  FormOrQuizSelector,
} from '../components/FormFields/FormOrQuizSelector';
import { DashboardContext } from '../contexts/DashboardContext';
import { SnackbarContext } from '../contexts/SnackbarContext';

const urlValidationConfig: UrlValidationConfig = {
  allowHashOnly: true,
  allowRelative: true,
  allowMailto: true,
  allowTel: true,
};

export type HtmlActionType = 'link' | 'form' | 'popup' | 'none';

export interface HtmlActionData {
  action: HtmlActionType;
  url?: string;
  /** ID of the form or quiz to open */
  formId?: string;
  /** ID of the popup to open */
  popupId?: string;
  isConversion?: boolean;
  openInNewTab?: boolean;
  customEventData?: Record<string, string>;
}

// Reserved keys that cannot be used as custom event attribute names
const RESERVED_CUSTOM_EVENT_KEYS = new Set([
  'id',
  'type',
  'path',
  'link',
  'rawHTML',
  'sectionId',
  'sectionType',
  'sectionIndex',
  'buttonId',
  'buttonPath',
  'buttonType',
  'buttonLink',
  'buttonVariant',
  'itemId',
  'itemIndex',
  'conversion',
]);

const MAX_CUSTOM_EVENTS = 10;
const MAX_KEY_LENGTH = 50;
const MAX_VALUE_LENGTH = 500;

interface Props {
  data?: HtmlActionData;
  open: boolean;
  onClose: () => void;
  onSave: (data: HtmlActionData) => Promise<void> | void;
  /** Available popups for the popup action type */
  popups?: { id: string; name: string }[];
}

const createDefaultHtmlAction = (): HtmlActionData => ({
  action: 'link',
  url: '',
  formId: '',
  popupId: '',
  isConversion: true,
  openInNewTab: false,
  customEventData: {},
});

export default function EditHtmlActionModal({
  data: initialData,
  open,
  onClose,
  onSave,
  popups,
}: Props) {
  const { show } = useContext(SnackbarContext);
  const { forms, quizzes } = useContext(DashboardContext);

  const [data, setData] = useState<HtmlActionData>(() =>
    createDefaultHtmlAction(),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error>();

  // Only reset form data when the modal opens, not on every initialData change
  // This prevents the form from resetting during parent re-renders (e.g., presence updates)
  useEffect(() => {
    if (!open) return;

    const newData = initialData
      ? JSON.parse(JSON.stringify(initialData))
      : createDefaultHtmlAction();
    if (newData.isConversion === undefined) {
      newData.isConversion = true;
    }
    if (newData.openInNewTab === undefined) {
      newData.openInNewTab = false;
    }
    setData(newData);
    setError(undefined);
    setIsSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const saveAction = useCallback(async () => {
    setError(undefined);

    if (data.action === 'link' && !data.url) {
      setError(new Error('Link URL is required'));
      show('Link URL is required');
      return;
    }

    if (
      data.action === 'link' &&
      data.url &&
      !isValidUrlWithParams(data.url, urlValidationConfig)
    ) {
      setError(new Error('Link URL is not valid'));
      show('Link URL is not valid');
      return;
    }

    if (data.action === 'form' && !data.formId) {
      setError(new Error('Please select a form or quiz'));
      show('Please select a form or quiz');
      return;
    }

    if (data.action === 'popup' && !data.popupId) {
      setError(new Error('Please select a popup'));
      show('Please select a popup');
      return;
    }

    if (
      data.action === 'form' &&
      data.formId &&
      !forms?.some((f) => f.id === data.formId) &&
      !quizzes?.some((q) => q.id === data.formId)
    ) {
      setError(new Error('Selected form or quiz not found'));
      show('Selected form or quiz not found');
      return;
    }

    // Validate custom event keys
    if (data.customEventData) {
      for (const key of Object.keys(data.customEventData)) {
        if (RESERVED_CUSTOM_EVENT_KEYS.has(key)) {
          setError(new Error(`"${key}" is a reserved attribute name`));
          show(`"${key}" is a reserved attribute name`);
          return;
        }
        if (key.length > MAX_KEY_LENGTH) {
          setError(
            new Error(
              `Attribute name "${key}" exceeds ${MAX_KEY_LENGTH} characters`,
            ),
          );
          show(`Attribute name exceeds ${MAX_KEY_LENGTH} characters`);
          return;
        }
        const value = data.customEventData[key];
        if (value && value.length > MAX_VALUE_LENGTH) {
          setError(
            new Error(
              `Value for "${key}" exceeds ${MAX_VALUE_LENGTH} characters`,
            ),
          );
          show(`Attribute value exceeds ${MAX_VALUE_LENGTH} characters`);
          return;
        }
      }
    }

    const res = onSave(data);
    if (res && 'then' in res) {
      setIsSaving(true);
      res.catch((e) => {
        setError(e);
        Sentry.captureException(e);
      });
      res.finally(() => setIsSaving(false));
    }
  }, [data, forms, quizzes, onSave, show]);

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit HTML Action</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="relative flex flex-col justify-start gap-1">
            <FieldTitle title="Action Type" />
            <Select
              value={data.action}
              onValueChange={(action: HtmlActionType) => {
                setData((d) => ({
                  ...d,
                  action,
                  // Clear related fields when changing action type
                  url: action === 'link' ? d.url : '',
                  formId: action === 'form' ? d.formId : '',
                  popupId: action === 'popup' ? d.popupId : '',
                }));
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select action type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="link">
                  <span className="flex items-center gap-2">
                    <LinkIcon className="h-4 w-4" />
                    Link
                  </span>
                </SelectItem>
                <SelectItem value="form">
                  <span className="flex items-center gap-2">
                    <UserIcon className="h-4 w-4" />
                    Form
                  </span>
                </SelectItem>
                {popups && popups.length > 0 && (
                  <SelectItem value="popup">
                    <span className="flex items-center gap-2">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        className="h-4 w-4 shrink-0"
                      >
                        <rect
                          x="2"
                          y="2"
                          width="12"
                          height="12"
                          rx="2"
                          stroke="#141C25"
                          strokeWidth="1.2"
                        />
                        <rect
                          x="4"
                          y="4"
                          width="8"
                          height="2"
                          rx="0.5"
                          fill="#141C25"
                        />
                      </svg>
                      Popup
                    </span>
                  </SelectItem>
                )}
                <SelectItem value="none">
                  <span className="flex items-center gap-2">
                    <CursorArrowRaysIcon className="h-4 w-4" />
                    None
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {data.action === 'link' && (
            <>
              <Input
                title="Link URL"
                value={data.url || ''}
                type="url"
                placeholder="https://example.com"
                onChange={(e) => {
                  setData((d) => ({
                    ...d,
                    url: e.target.value,
                  }));
                }}
                validation={
                  data.url
                    ? formFieldUrlWithParamsValidator(
                        data.url,
                        true,
                        urlValidationConfig,
                      )
                    : undefined
                }
              />

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-900">
                    Open in New Tab
                  </label>
                  <p className="text-sm text-gray-500">
                    Open the link in a new browser tab
                  </p>
                </div>
                <Switch
                  checked={data.openInNewTab}
                  onCheckedChange={(checked: boolean) => {
                    setData((d) => ({
                      ...d,
                      openInNewTab: checked,
                    }));
                  }}
                />
              </div>
            </>
          )}

          {data.action === 'form' && (
            <FormOrQuizSelector
              title="Form or Quiz"
              formId={data.formId}
              onChange={(selection: FormOrQuizSelection) => {
                setData((d) => ({
                  ...d,
                  formId: selection?.id ?? '',
                }));
              }}
            />
          )}

          {data.action === 'popup' && popups && (
            <div className="relative flex flex-col justify-start gap-1">
              <FieldTitle title="Popup" />
              <Select
                value={data.popupId || ''}
                onValueChange={(popupId: string) => {
                  setData((d) => ({ ...d, popupId }));
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a popup" />
                </SelectTrigger>
                <SelectContent>
                  {popups.map((popup) => (
                    <SelectItem key={popup.id} value={popup.id}>
                      {popup.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-900">
                Track as Conversion
              </label>
              <p className="text-sm text-gray-500">
                Enable conversion tracking for this action
              </p>
            </div>
            <Switch
              checked={data.isConversion}
              onCheckedChange={(checked: boolean) => {
                setData((d) => ({
                  ...d,
                  isConversion: checked,
                }));
              }}
            />
          </div>

          {/* Advanced Settings */}
          <Collapsible>
            <CollapsibleTrigger className="group flex w-full items-center justify-between py-2 text-sm font-medium text-gray-700 hover:text-gray-900">
              <span>Advanced</span>
              <ChevronDownIcon className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent className="data-[state=closed]:animate-collapse-up data-[state=open]:animate-collapse-down overflow-hidden">
              {/* Custom Event Attributes */}
              <div className="flex flex-col gap-2 pt-2">
                <div className="flex items-center justify-between">
                  <FieldTitle title="Custom Event Attributes" />
                  <Button
                    variant="ghost"
                    color="primary"
                    size="sm"
                    onClick={() => {
                      const currentCount = Object.keys(
                        data.customEventData || {},
                      ).length;
                      if (currentCount >= MAX_CUSTOM_EVENTS) {
                        show(
                          `Maximum ${MAX_CUSTOM_EVENTS} custom attributes allowed`,
                        );
                        return;
                      }
                      setData((d) => ({
                        ...d,
                        customEventData: {
                          ...d.customEventData,
                          '': '',
                        },
                      }));
                    }}
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Attribute
                  </Button>
                </div>
                {Object.entries(data.customEventData || {}).map(
                  ([key, value], index) => (
                    <div
                      // eslint-disable-next-line react/no-array-index-key
                      key={`custom-event-${index}`}
                      className="flex items-center gap-2"
                    >
                      <Input
                        placeholder="Key"
                        value={key}
                        onChange={(e) => {
                          const newKey = e.target.value;
                          setData((d) => {
                            const entries = Object.entries(
                              d.customEventData || {},
                            );
                            entries[index] = [newKey, value];
                            return {
                              ...d,
                              customEventData: Object.fromEntries(entries),
                            };
                          });
                        }}
                        validation={
                          RESERVED_CUSTOM_EVENT_KEYS.has(key)
                            ? { status: 'error', message: 'Reserved key' }
                            : key.length > MAX_KEY_LENGTH
                              ? { status: 'error', message: 'Key too long' }
                              : undefined
                        }
                        className="flex-1"
                      />
                      <Input
                        placeholder="Value"
                        value={value}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setData((d) => ({
                            ...d,
                            customEventData: {
                              ...d.customEventData,
                              [key]: newValue,
                            },
                          }));
                        }}
                        validation={
                          value.length > MAX_VALUE_LENGTH
                            ? { status: 'error', message: 'Value too long' }
                            : undefined
                        }
                        className="flex-1"
                      />
                      <Button
                        size="icon-lg"
                        variant="ghost"
                        color="negative"
                        onClick={() => {
                          setData((d) => {
                            const newCustomEventData = { ...d.customEventData };
                            delete newCustomEventData[key];
                            return {
                              ...d,
                              customEventData: newCustomEventData,
                            };
                          });
                        }}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  ),
                )}
                {Object.keys(data.customEventData || {}).length === 0 && (
                  <p className="text-sm text-gray-500">
                    Add custom attributes to track additional data with this
                    action.
                  </p>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {error && (
            <div className="w-full text-end text-red-600">
              {error.message || 'Failed to save'}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={saveAction} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
